import React, { useState, useEffect, useCallback, useRef } from "react";
import { Canvas, Rect, Circle, Line, IText, Textbox, FabricImage } from "fabric";
import {
  Box,
  Button,
  Stack,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  Divider,
  TextField,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Delete,
  FlipToFront,
  FlipToBack,
  CenterFocusStrong,
  TextFields,
  Image as ImageIcon,
  Star,
  BorderOuter,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Lock,
  LockOpen,
  Visibility,
  VisibilityOff,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import CertificateCanvas from "./CertificateCanvas";

// Supported fonts
const AVAILABLE_FONTS = [
  "Inter",
  "Georgia",
  "Playfair Display",
  "Montserrat",
  "Courier New",
  "Arial",
];

// Available ERP tokens
const ERP_TOKENS = [
  { name: "Student Name", token: "{{student_name}}", desc: "Full name of the student" },
  { name: "Roll Number", token: "{{roll_no}}", desc: "Roll number assigned to student" },
  { name: "Class Name", token: "{{class_name}}", desc: "Class / Grade (e.g. Class 10th)" },
  { name: "Session Year", token: "{{session_name}}", desc: "Active Session (e.g. 2025-26)" },
  { name: "Father's Name", token: "{{father_name}}", desc: "Father / Guardian's name" },
  { name: "Date of Birth", token: "{{dob}}", desc: "Student's birth date" },
  { name: "Issue Date", token: "{{issue_date}}", desc: "Date certificate is generated" },
];

interface Props {
  editorKey: string;
  initialJson: string | null;
  onAutoSave?: (json: string, svg: string) => void;
  onCanvasReady?: (canvas: Canvas) => void;
  width?: number;
  height?: number;
}

export default function CertificateTemplateBuilderEditor({
  editorKey,
  initialJson,
  onAutoSave,
  onCanvasReady,
  width = 842,
  height = 595,
}: Props) {
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Styling States
  const [fontFamily, setFontFamily] = useState("Inter");
  const [fontSize, setFontSize] = useState(24);
  const [fontColor, setFontColor] = useState("#1e293b");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [textAlign, setTextAlign] = useState("left");

  // History State Stacks for Undo/Redo
  const undoStack = useRef<string[]>([]);
  const redoStack = useRef<string[]>([]);
  const isUndoRedoAction = useRef<boolean>(false);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const [layers, setLayers] = useState<any[]>([]);

  // Drag and drop states for layers reordering
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex || !canvas) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newLayers = [...layers];
    const [draggedItem] = newLayers.splice(draggedIndex, 1);
    newLayers.splice(targetIndex, 0, draggedItem);

    // Reorder objects on the fabric canvas based on our newLayers array.
    newLayers.slice().reverse().forEach((obj, idx) => {
      canvas.moveObjectTo(obj, idx);
    });

    canvas.renderAll();
    canvas.fire("object:modified");
    refreshLayers(canvas);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const refreshLayers = useCallback((fabricCanvas: Canvas) => {
    if (!fabricCanvas) return;
    // Retrieve objects and reverse them so top layers appear first
    const currentObjects = [...fabricCanvas.getObjects()].reverse();
    setLayers(currentObjects);
  }, []);

  const updateHistoryStates = () => {
    setCanUndo(undoStack.current.length > 1);
    setCanRedo(redoStack.current.length > 0);
  };

  // Load initial template if present
  const handleCanvasReady = useCallback(
    (fabricCanvas: Canvas) => {
      setCanvas(fabricCanvas);

      const saveInitialState = () => {
        const json = JSON.stringify(fabricCanvas.toJSON());
        undoStack.current = [json];
        redoStack.current = [];
        updateHistoryStates();
        refreshLayers(fabricCanvas);
      };

      if (initialJson) {
        try {
          fabricCanvas.loadFromJSON(JSON.parse(initialJson)).then(() => {
            fabricCanvas.renderAll();
            saveInitialState();
          }).catch((err: any) => {
            console.error("Failed to load and render initial layout JSON:", err);
            saveInitialState();
          });
        } catch (e) {
          console.error("Failed to parse initial layout JSON:", e);
          saveInitialState();
        }
      } else {
        saveInitialState();
      }

      // Event: selection updates
      fabricCanvas.on("selection:created", (e: any) => handleSelection(e.selected?.[0]));
      fabricCanvas.on("selection:updated", (e: any) => handleSelection(e.selected?.[0]));
      fabricCanvas.on("selection:cleared", () => handleSelection(null));

      // Event: modifications (triggers auto-save)
      const triggerSave = () => {
        if (onAutoSave) {
          const json = JSON.stringify(fabricCanvas.toJSON());
          const svg = fabricCanvas.toSVG();
          onAutoSave(json, svg);
        }
      };

      const saveStateToHistory = () => {
        if (isUndoRedoAction.current) return;
        const json = JSON.stringify(fabricCanvas.toJSON());
        
        // Prevent duplicate consecutive states
        if (undoStack.current.length > 0 && undoStack.current[undoStack.current.length - 1] === json) {
          return;
        }

        undoStack.current.push(json);
        redoStack.current = [];
        updateHistoryStates();
        refreshLayers(fabricCanvas);
      };

      const handleCanvasEvents = () => {
        saveStateToHistory();
        triggerSave();
      };

      fabricCanvas.on("object:modified", handleCanvasEvents);
      fabricCanvas.on("object:added", handleCanvasEvents);
      fabricCanvas.on("object:removed", handleCanvasEvents);

      if (onCanvasReady) {
        onCanvasReady(fabricCanvas);
      }
    },
    [initialJson, onAutoSave, onCanvasReady]
  );

  // Undo & Redo logic
  const undo = () => {
    if (!canvas || undoStack.current.length <= 1) return;

    const currentState = undoStack.current.pop();
    if (currentState) {
      redoStack.current.push(currentState);
    }

    const prevState = undoStack.current[undoStack.current.length - 1];
    isUndoRedoAction.current = true;
    canvas.loadFromJSON(JSON.parse(prevState)).then(() => {
      canvas.renderAll();
      isUndoRedoAction.current = false;
      updateHistoryStates();
      refreshLayers(canvas);

      // Clear selection upon undo to prevent toolbar control mismatch
      canvas.discardActiveObject();
      setSelectedObject(null);

      if (onAutoSave) {
        onAutoSave(prevState, canvas.toSVG());
      }
    }).catch((err: any) => {
      console.error("Undo failed:", err);
      isUndoRedoAction.current = false;
    });
  };

  const redo = () => {
    if (!canvas || redoStack.current.length === 0) return;

    const nextState = redoStack.current.pop();
    if (nextState) {
      undoStack.current.push(nextState);

      isUndoRedoAction.current = true;
      canvas.loadFromJSON(JSON.parse(nextState)).then(() => {
        canvas.renderAll();
        isUndoRedoAction.current = false;
        updateHistoryStates();
        refreshLayers(canvas);

        // Clear selection upon redo to prevent toolbar control mismatch
        canvas.discardActiveObject();
        setSelectedObject(null);

        if (onAutoSave) {
          onAutoSave(nextState, canvas.toSVG());
        }
      }).catch((err: any) => {
        console.error("Redo failed:", err);
        isUndoRedoAction.current = false;
      });
    }
  };

  // Layers controls & actions
  const toggleLock = (obj: any) => {
    if (!canvas) return;
    const makeLocked = obj.selectable !== false;
    obj.set({
      selectable: !makeLocked,
      evented: !makeLocked,
    });
    if (makeLocked && canvas.getActiveObject() === obj) {
      canvas.discardActiveObject();
      setSelectedObject(null);
    }
    canvas.renderAll();
    canvas.fire("object:modified");
    refreshLayers(canvas);
  };

  const toggleVisibility = (obj: any) => {
    if (!canvas) return;
    const makeHidden = obj.visible !== false;
    obj.set({
      visible: !makeHidden,
    });
    if (makeHidden && canvas.getActiveObject() === obj) {
      canvas.discardActiveObject();
      setSelectedObject(null);
    }
    canvas.renderAll();
    canvas.fire("object:modified");
    refreshLayers(canvas);
  };

  const moveLayerUp = (obj: any) => {
    if (!canvas) return;
    canvas.bringObjectForward(obj);
    canvas.renderAll();
    canvas.fire("object:modified");
    refreshLayers(canvas);
  };

  const moveLayerDown = (obj: any) => {
    if (!canvas) return;
    canvas.sendObjectBackwards(obj);
    canvas.renderAll();
    canvas.fire("object:modified");
    refreshLayers(canvas);
  };

  const deleteLayer = (obj: any) => {
    if (!canvas) return;
    canvas.remove(obj);
    if (canvas.getActiveObject() === obj) {
      canvas.discardActiveObject();
      setSelectedObject(null);
    }
    canvas.renderAll();
    // object:removed fires handleCanvasEvents, which automatically calls refreshLayers and triggerSave
  };

  const getLayerLabel = (obj: any) => {
    if (!obj) return "Unknown Layer";
    if (obj.type === "i-text" || obj.type === "textbox") {
      const textVal = obj.text || "";
      const font = obj.fontFamily || "Inter";
      if (textVal.startsWith("{{") && textVal.endsWith("}}")) {
        return `Token: ${textVal}`;
      }
      const truncated = textVal.length > 15 ? textVal.substring(0, 15) + "..." : textVal;
      return `Text (${font}): "${truncated}"`;
    }
    if (obj.type === "rect") {
      if (obj.stroke === "#1e3a8a") return "Blue Outer Border";
      if (obj.stroke === "#d97706") return "Gold Inner Border";
      return `Rectangle (${obj.width}x${obj.height})`;
    }
    if (obj.type === "circle") {
      if (obj.stroke === "#d97706") return "Gold Seal Circle";
      return `Circle (Radius: ${obj.radius})`;
    }
    if (obj.type === "line") {
      return "Separator Line";
    }
    if (obj.type === "image" || obj.type === "fabric-image") {
      return "Uploaded Image / Seal";
    }
    return `${obj.type.charAt(0).toUpperCase() + obj.type.slice(1)} Element`;
  };

  const handleSelection = (obj: any) => {
    setSelectedObject(obj);
    if (!obj) return;

    // Pull styles from selected text object
    if (obj.type === "i-text" || obj.type === "textbox") {
      setFontFamily(obj.fontFamily || "Inter");
      setFontSize(obj.fontSize || 24);
      setFontColor(obj.fill || "#1e293b");
      setIsBold(obj.fontWeight === "bold");
      setIsItalic(obj.fontStyle === "italic");
      setIsUnderline(obj.underline || false);
      setTextAlign(obj.textAlign || "left");
    }
  };

  // Editor Actions
  const addIText = (text: string) => {
    if (!canvas) return;
    const item = new IText(text, {
      left: width / 2 - 100,
      top: height / 2 - 20,
      fontFamily,
      fontSize: 32,
      fill: fontColor,
      fontWeight: "bold",
    });
    canvas.add(item);
    canvas.setActiveObject(item);
    canvas.renderAll();
  };

  const addTextbox = (text: string) => {
    if (!canvas) return;
    const item = new Textbox(text, {
      left: width / 2 - 150,
      top: height / 2,
      width: 300,
      fontFamily,
      fontSize: 20,
      fill: fontColor,
      textAlign: "center",
    });
    canvas.add(item);
    canvas.setActiveObject(item);
    canvas.renderAll();
  };

  const addToken = (token: string) => {
    if (!canvas) return;
    const item = new IText(token, {
      left: width / 2 - 80,
      top: height / 2 + 50,
      fontFamily: "Georgia",
      fontSize: 24,
      fill: "#1e3a8a",
      fontWeight: "bold",
    });
    canvas.add(item);
    canvas.setActiveObject(item);
    canvas.renderAll();
  };

  const addRect = () => {
    if (!canvas) return;
    const item = new Rect({
      left: width / 2 - 50,
      top: height / 2 - 50,
      width: 100,
      height: 100,
      fill: "transparent",
      stroke: "#cbd5e1",
      strokeWidth: 4,
    });
    canvas.add(item);
    canvas.setActiveObject(item);
    canvas.renderAll();
  };

  const addCircle = () => {
    if (!canvas) return;
    const item = new Circle({
      left: width / 2 - 50,
      top: height / 2 - 50,
      radius: 50,
      fill: "transparent",
      stroke: "#f59e0b",
      strokeWidth: 4,
    });
    canvas.add(item);
    canvas.setActiveObject(item);
    canvas.renderAll();
  };

  const addBorderDecoration = () => {
    if (!canvas) return;
    const border = new Rect({
      left: 15,
      top: 15,
      width: width - 30,
      height: height - 30,
      fill: "transparent",
      stroke: "#1e3a8a",
      strokeWidth: 6,
      selectable: true,
      excludeFromExport: false,
    });
    const innerBorder = new Rect({
      left: 23,
      top: 23,
      width: width - 46,
      height: height - 46,
      fill: "transparent",
      stroke: "#d97706",
      strokeWidth: 2,
      selectable: true,
    });
    canvas.add(border);
    canvas.add(innerBorder);
    canvas.renderAll();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !canvas) return;

    const reader = new FileReader();
    reader.onload = (f) => {
      const dataUrl = f.target?.result as string;
      // Modern Fabric v7.4.0 static Image loader
      FabricImage.fromURL(dataUrl).then((img: any) => {
        img.scaleToWidth(200);
        img.set({
          left: width / 2 - 100,
          top: height / 2 - 100,
        });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
      });
    };
    reader.readAsDataURL(file);
  };

  // Alignment Centering APIs
  const alignCenterHorizontal = () => {
    if (!canvas || !selectedObject) return;
    canvas.centerObjectH(selectedObject);
    canvas.renderAll();
    // Fire modify to trigger auto-save
    canvas.fire("object:modified");
  };

  const alignCenterVertical = () => {
    if (!canvas || !selectedObject) return;
    canvas.centerObjectV(selectedObject);
    canvas.renderAll();
    canvas.fire("object:modified");
  };

  // Object Property Modifiers
  const updateStyle = (key: string, value: any) => {
    if (!canvas || !selectedObject) return;
    selectedObject.set(key, value);
    canvas.renderAll();
    canvas.fire("object:modified");
  };

  const deleteSelected = () => {
    if (!canvas || !selectedObject) return;
    canvas.remove(selectedObject);
    canvas.discardActiveObject();
    setSelectedObject(null);
    canvas.renderAll();
  };

  const bringToFront = () => {
    if (!canvas || !selectedObject) return;
    canvas.bringObjectToFront(selectedObject);
    canvas.renderAll();
    canvas.fire("object:modified");
  };

  const sendToBack = () => {
    if (!canvas || !selectedObject) return;
    canvas.sendObjectToBack(selectedObject);
    canvas.renderAll();
    canvas.fire("object:modified");
  };

  return (
    <Box sx={{ display: "flex", flex: 1, height: "100%", overflow: "hidden" }}>
      {/* 1. Designer Sidebar Toolkit */}
      <Paper
        elevation={0}
        sx={{
          width: 320,
          borderRight: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="fullWidth"
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
        >
          <Tab label="Elements" style={{ fontSize: 12, fontWeight: 600 }} />
          <Tab label="Tokens" style={{ fontSize: 12, fontWeight: 600 }} />
          <Tab label="Layers" style={{ fontSize: 12, fontWeight: 600 }} />
        </Tabs>

        <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
          {activeTab === 0 && (
            <Stack spacing={2.5}>
              {/* Text Elements */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Text Assets
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<TextFields />}
                    size="small"
                    onClick={() => addIText("Heading Title")}
                    fullWidth
                  >
                    Add Heading
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TextFields />}
                    size="small"
                    onClick={() => addTextbox("Appreciation body text message goes here.")}
                    fullWidth
                  >
                    Add Paragraph
                  </Button>
                </Stack>
              </Box>

              <Divider />

              {/* Graphic Assets */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Borders & Ornaments
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<BorderOuter />}
                    onClick={addBorderDecoration}
                    size="small"
                    fullWidth
                  >
                    Double Line Page Border
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" onClick={addRect} fullWidth>
                      Rectangle
                    </Button>
                    <Button variant="outlined" size="small" onClick={addCircle} fullWidth>
                      Gold Seal Circle
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* Image Uploaders */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Logo & Signature Images
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<ImageIcon />}
                  size="small"
                  fullWidth
                >
                  Upload Local Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Supports transparent PNGs for seals and signatures.
                </Typography>
              </Box>
            </Stack>
          )}

          {activeTab === 1 && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                ERP Student Fields
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add these merge tokens to the certificate. They will automatically fill with real student details on printing.
              </Typography>
              {ERP_TOKENS.map((tk) => (
                <Paper
                  key={tk.token}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: "primary.main", bgcolor: "rgba(30,58,138,0.02)" },
                  }}
                  onClick={() => addToken(tk.token)}
                >
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    {tk.name}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block" }}>
                    Code: {tk.token}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" fontSize={10}>
                    {tk.desc}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}

          {activeTab === 2 && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                Canvas Layers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click any item to select it. Lock background borders so they don't block you from selecting text above them.
              </Typography>
              
              {layers.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    No elements on the canvas yet.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {layers.map((layer, index) => {
                    const isSelected = selectedObject === layer;
                    const isLocked = layer.selectable === false;
                    const isHidden = layer.visible === false;
                    
                    return (
                       <Paper
                         key={index}
                         variant="outlined"
                         draggable
                         onDragStart={(e) => handleDragStart(e, index)}
                         onDragOver={(e) => handleDragOver(e, index)}
                         onDragLeave={handleDragLeave}
                         onDrop={(e) => handleDrop(e, index)}
                         onDragEnd={() => {
                           setDraggedIndex(null);
                           setDragOverIndex(null);
                         }}
                         sx={{
                           p: 1,
                           display: "flex",
                           alignItems: "center",
                           gap: 1,
                           borderColor: dragOverIndex === index
                             ? "secondary.main"
                             : isSelected
                             ? "primary.main"
                             : "divider",
                           borderWidth: dragOverIndex === index ? 2 : 1,
                           borderStyle: dragOverIndex === index ? "dashed" : "solid",
                           bgcolor: dragOverIndex === index
                             ? "rgba(245,158,11,0.05)"
                             : isSelected
                             ? "rgba(30,58,138,0.02)"
                             : "background.paper",
                           opacity: draggedIndex === index ? 0.4 : 1,
                           cursor: "grab",
                           transition: "all 0.15s ease",
                           "&:hover": {
                             borderColor: "primary.main",
                           },
                         }}
                         onClick={() => {
                           if (!canvas) return;
                           canvas.setActiveObject(layer);
                           canvas.renderAll();
                           setSelectedObject(layer);
                         }}
                       >
                         {/* Friendly Layer Label */}
                         <Box sx={{ flex: 1, overflow: "hidden" }}>
                           <Typography variant="caption" fontWeight={600} display="block" noWrap>
                             {getLayerLabel(layer)}
                           </Typography>
                         </Box>
                         
                         {/* Layer Stacking Up/Down */}
                         <Tooltip title="Move Up (Z-Index)">
                           <span>
                             <IconButton
                               size="small"
                               disabled={index === 0}
                               onClick={(e) => {
                                 e.stopPropagation();
                                 moveLayerUp(layer);
                               }}
                             >
                               <ArrowUpward fontSize="small" style={{ fontSize: 15 }} />
                             </IconButton>
                           </span>
                         </Tooltip>
                         <Tooltip title="Move Down (Z-Index)">
                           <span>
                             <IconButton
                               size="small"
                               disabled={index === layers.length - 1}
                               onClick={(e) => {
                                 e.stopPropagation();
                                 moveLayerDown(layer);
                               }}
                             >
                               <ArrowDownward fontSize="small" style={{ fontSize: 15 }} />
                             </IconButton>
                           </span>
                         </Tooltip>
                         
                         {/* Lock/Unlock Toggle */}
                         <Tooltip title={isLocked ? "Unlock Layer" : "Lock Layer (Prevents clicking/dragging on canvas)"}>
                           <IconButton
                             size="small"
                             color={isLocked ? "warning" : "default"}
                             onClick={(e) => {
                               e.stopPropagation();
                               toggleLock(layer);
                             }}
                           >
                             {isLocked ? (
                               <Lock fontSize="small" style={{ fontSize: 15 }} />
                             ) : (
                               <LockOpen fontSize="small" style={{ fontSize: 15 }} />
                             )}
                           </IconButton>
                         </Tooltip>
                         
                         {/* Hide/Show Toggle */}
                         <Tooltip title={isHidden ? "Show Layer" : "Hide Layer"}>
                           <IconButton
                             size="small"
                             color={isHidden ? "error" : "default"}
                             onClick={(e) => {
                               e.stopPropagation();
                               toggleVisibility(layer);
                             }}
                           >
                             {isHidden ? (
                               <VisibilityOff fontSize="small" style={{ fontSize: 15 }} />
                             ) : (
                               <Visibility fontSize="small" style={{ fontSize: 15 }} />
                             )}
                           </IconButton>
                         </Tooltip>
                         
                         {/* Delete Layer */}
                         <Tooltip title="Delete Element">
                           <IconButton
                             size="small"
                             color="error"
                             onClick={(e) => {
                               e.stopPropagation();
                               deleteLayer(layer);
                             }}
                           >
                             <Delete fontSize="small" style={{ fontSize: 15 }} />
                           </IconButton>
                         </Tooltip>
                       </Paper>
                     );
                  })}
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      {/* 2. Designer Canvas Area */}
      <Box sx={{ flex: 1, bgcolor: "#f1f5f9", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Styling controls toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            bgcolor: "#ffffff",
          }}
        >
          {/* History Undo / Redo Actions */}
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Undo">
              <span>
                <IconButton size="small" onClick={undo} disabled={!canUndo}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo">
              <span>
                <IconButton size="small" onClick={redo} disabled={!canRedo}>
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: "center" }} />

          {selectedObject && (selectedObject.type === "i-text" || selectedObject.type === "textbox") ? (
            <>
              {/* Font Selection */}
              <Select
                size="small"
                value={fontFamily}
                onChange={(e) => {
                  setFontFamily(e.target.value);
                  updateStyle("fontFamily", e.target.value);
                }}
                sx={{ width: 140, height: 32, fontSize: 13 }}
              >
                {AVAILABLE_FONTS.map((font) => (
                  <MenuItem key={font} value={font} style={{ fontFamily: font, fontSize: 13 }}>
                    {font}
                  </MenuItem>
                ))}
              </Select>

              {/* Font Size */}
              <TextField
                type="number"
                size="small"
                value={fontSize}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 12;
                  setFontSize(val);
                  updateStyle("fontSize", val);
                }}
                inputProps={{ min: 8, max: 120 }}
                sx={{
                  width: 65,
                  "& .MuiOutlinedInput-root": { height: 32, fontSize: 13 },
                }}
              />

              {/* Color Picker */}
              <input
                type="color"
                value={fontColor}
                onChange={(e) => {
                  setFontColor(e.target.value);
                  updateStyle("fill", e.target.value);
                }}
                style={{
                  width: 32,
                  height: 32,
                  border: "1px solid #cbd5e1",
                  borderRadius: 4,
                  cursor: "pointer",
                  padding: 0,
                  backgroundColor: "transparent",
                }}
              />

              <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: "center" }} />

              {/* Bold, Italic, Underline */}
              <Stack direction="row" spacing={0.2}>
                <Tooltip title="Bold">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsBold(!isBold);
                      updateStyle("fontWeight", !isBold ? "bold" : "normal");
                    }}
                    color={isBold ? "primary" : "default"}
                  >
                    <FormatBold fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsItalic(!isItalic);
                      updateStyle("fontStyle", !isItalic ? "italic" : "normal");
                    }}
                    color={isItalic ? "primary" : "default"}
                  >
                    <FormatItalic fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Underline">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsUnderline(!isUnderline);
                      updateStyle("underline", !isUnderline);
                    }}
                    color={isUnderline ? "primary" : "default"}
                  >
                    <FormatUnderlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: "center" }} />

              {/* Text Alignments */}
              <Stack direction="row" spacing={0.2}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setTextAlign("left");
                    updateStyle("textAlign", "left");
                  }}
                  color={textAlign === "left" ? "primary" : "default"}
                >
                  <FormatAlignLeft fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setTextAlign("center");
                    updateStyle("textAlign", "center");
                  }}
                  color={textAlign === "center" ? "primary" : "default"}
                >
                  <FormatAlignCenter fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setTextAlign("right");
                    updateStyle("textAlign", "right");
                  }}
                  color={textAlign === "right" ? "primary" : "default"}
                >
                  <FormatAlignRight fontSize="small" />
                </IconButton>
              </Stack>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select any object or text block to modify its properties.
            </Typography>
          )}

          {selectedObject && (
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              <Tooltip title="Center Horizontally">
                <IconButton size="small" onClick={alignCenterHorizontal}>
                  <CenterFocusStrong fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Center Vertically">
                <IconButton size="small" onClick={alignCenterVertical}>
                  <CenterFocusStrong fontSize="small" style={{ transform: "rotate(90deg)" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bring to Front">
                <IconButton size="small" onClick={bringToFront}>
                  <FlipToFront fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send to Back">
                <IconButton size="small" onClick={sendToBack}>
                  <FlipToBack fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Object">
                <IconButton size="small" color="error" onClick={deleteSelected}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Paper>

        {/* Canvas viewport container */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "auto",
            p: 3,
          }}
        >
          <CertificateCanvas
            onCanvasReady={handleCanvasReady}
            width={width}
            height={height}
          />
        </Box>
      </Box>
    </Box>
  );
}
